import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CkThemeProvider, CkToastProvider, PageHeader, DataTable, Segmented, SearchInput,
  Tag, Button, Banner, Sheet, Icon, Popover, usePopover, exportRows, applyClientQuery,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../../services/api.js';
import { formatHebrewDateTime } from '../../utils/hebrewDate';
import MemberCardNext from '../members/MemberCardNext.jsx';
import StudyStatusDialogNext from '../../components/StudyStatusDialogNext.jsx';
import MessageBroadcastDialog from '../../components/MessageBroadcastDialog.jsx';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { STUDY_FORMATS, isWorking, partnerLabel, studyMissing } from '../../utils/memberStudy.js';
import { EXAMINEES_FRAME_LABELS } from './ExamineesDashboard.jsx';
import { apiError } from '../../next/ui';

/**
 * דו"ח נבחנים — matrix of examinee × exam (did they take it, and the score), from /american-exams/examinees-report.
 * All filtering is client side, from one bar of dropdowns: track, which exams (last N per track / all, or any exams
 * ticked in a searchable list), a date range (only attempts finished in it count, only exams taken in it are offered),
 * which people (took a shown exam / ever took an exam in the track / every member), and the table's own search and
 * column filters. The stats strip summarises exactly the rows that pass those filters; three of its items filter by coverage.
 * Reps track each examinee's status here (occupation; for עמל הפרנסה — חברותא / שיעור and with whom), edited in a dialog.
 * "שיגור הודעה" sends a voice message to exactly the rows that pass the filters.
 */
const STORE_KEY = 'bchinot-examinees-report-filters';
const FRAMES = Object.entries(EXAMINEES_FRAME_LABELS).map(([value, label]) => ({ value, label }));
const LAST_OPTIONS = [
  { value: '1', label: 'אחרון' },
  { value: '2', label: '2 אחרונים' },
  { value: '3', label: '3 אחרונים' },
  { value: '5', label: '5 אחרונים' },
  { value: '10', label: '10 אחרונים' },
  { value: 'all', label: 'הכל' },
];
const DATE_PRESETS = [
  { key: '', label: 'כל התאריכים', days: null },
  { key: '30', label: '30 הימים האחרונים', days: 30 },
  { key: '90', label: '3 החודשים האחרונים', days: 90 },
  { key: '365', label: 'השנה האחרונה', days: 365 },
];
const POPULATIONS = [
  { value: 'shown', label: 'נבחנו במבחנים המוצגים', hint: 'רק מי שנבחן לפחות באחד מהמבחנים שבדו״ח' },
  { value: 'ever', label: 'נבחנו אי פעם במסלול', hint: 'כולל מי שלא נבחן במבחנים שבדו״ח — כדי לראות מי לא הגיע' },
  { value: 'all', label: 'כל החברים', hint: 'כל החברים הפעילים, גם מי שמעולם לא נבחן' },
];
const CELL_MODES = [
  { value: 'check', label: 'סימון' },
  { value: 'score', label: 'ציון' },
];
const COVERAGE = {
  full: 'נבחנו בכל המבחנים',
  partial: 'נבחנו בחלק מהמבחנים',
  none: 'לא נבחנו באף מבחן',
};
const CHANNEL_LABELS = { web: 'פורטל', ivr: 'טלפון', import: 'ייבוא' };
const DEFAULTS = {
  frame: 'daf_yomi', last: '3', picked: [], from: '', to: '', population: 'ever', cellMode: 'check',
};
const EMPTY_QUERY = { page: 1, pageSize: 50, search: '', sort: null, filters: {} };
const EXAM_KEY_PREFIX = 'ex_';

const num = (v) => Number(v || 0).toLocaleString('he-IL');
const pct = (part, whole) => (whole ? `${Math.round((part / whole) * 100)}%` : '—');
const memberName = (m) => [m.firstName, m.lastName].filter(Boolean).join(' ') || '—';
const scoreText = (res) => (res.x ? `${res.s}/${res.x}` : String(res.s));
const isHigh = (res) => (res.x ? res.s / res.x >= 0.9 : res.s >= 90);
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return isoDay(d); };
const fmtDay = (iso) => (iso ? iso.split('-').reverse().join('/') : '…');

function readStored() {
  try {
    // examSearch / last:'pick' — from the first version of the screen
    const { examSearch, ...stored } = { ...DEFAULTS, ...JSON.parse(localStorage.getItem(STORE_KEY) || '{}') };
    return stored.last === 'pick' ? { ...stored, last: 'custom' } : stored;
  } catch {
    return DEFAULTS;
  }
}

/** the server sends each result as [examIndex, score, maxScore, channel, finishedAtMs, attempts] — make it { [examId]: {s,x,c,f,n} } */
function expandReport(res) {
  const exams = res?.exams || [];
  const members = (res?.members || []).map((m) => {
    const r = {};
    (m.r || []).forEach(([i, s, x, c, f, n]) => {
      if (exams[i]) r[exams[i].id] = { s, x, c, f: f ? new Date(f).toISOString() : null, n };
    });
    return { ...m, r };
  });
  return { ...res, exams, members };
}

// "דף היומי — מבחן 50 — קידושין ז - כ" → "מבחן 50 · קידושין ז - כ" (the track is shown separately)
function shortExamTitle(exam, withFrame) {
  const parts = String(exam.title || 'מבחן').split(' — ');
  if (parts.length > 1 && parts[0].trim() === exam.frameLabel) parts.shift();
  const label = parts.join(' · ');
  return withFrame ? `${exam.frameLabel} · ${label}` : label;
}

/** every word of the search appears in the exam title */
const titleMatches = (exam, search) => {
  const words = search.trim().split(/\s+/).filter(Boolean);
  return words.every((w) => exam.title.includes(w));
};

/** the exams shown as columns: newest first; "last N" counts per track when all tracks are shown */
function pickExams(inTrack, { last, picked }) {
  if (last === 'custom') {
    const set = new Set(picked);
    return inTrack.filter((e) => set.has(e.id));
  }
  if (last === 'all') return inTrack;
  const n = Number(last) || 1;
  const seen = {};
  return inTrack.filter((e) => {
    seen[e.frame] = (seen[e.frame] || 0) + 1;
    return seen[e.frame] <= n;
  });
}

/** a finished attempt inside the from–to range (dates are YYYY-MM-DD, local days) */
function inDateRange(res, from, to) {
  if (!from && !to) return true;
  if (!res.f) return false;
  const t = new Date(res.f).getTime();
  if (from && t < new Date(`${from}T00:00:00`).getTime()) return false;
  if (to && t > new Date(`${to}T23:59:59.999`).getTime()) return false;
  return true;
}

/** a filter in the bar: "label: value ▾" — opens its own popover; children may be (close) => node */
function FilterButton({ label, value, changed, width = 280, children }) {
  const pop = usePopover();
  return (
    <>
      <button
        type="button"
        className={`nx-fbtn${changed ? ' is-set' : ''}${pop.open ? ' is-open' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={pop.open}
        onClick={pop.toggle}
      >
        <span className="nx-fbtn__label">{label}</span>
        <span className="nx-fbtn__value">{value}</span>
        <Icon name="down" size={13} />
      </button>
      <Popover {...pop.props} width={width} ariaLabel={label}>
        {typeof children === 'function' ? children(pop.close) : children}
      </Popover>
    </>
  );
}

/** single choice inside a popover */
function OptionList({ options, value, onPick }) {
  return (
    <div className="nx-fopts" role="listbox">
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button key={String(o.value)} type="button" role="option" aria-selected={on} className={`nx-fopt${on ? ' is-on' : ''}`} onClick={() => onPick(o.value)}>
            <span className="nx-fopt__check">{on ? <Icon name="check" size={14} /> : null}</span>
            <span className="nx-fopt__text">
              <span>{o.label}</span>
              {o.hint ? <small>{o.hint}</small> : null}
            </span>
            {o.count != null ? <span className="nx-fopt__count">{num(o.count)}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

/** the exam picker: quick "last N", a search, and a checklist of every exam in the track */
function ExamPicker({ exams, shownIds, last, withFrame, takenCount, onPreset, onPick }) {
  const [q, setQ] = useState('');
  const search = q.trim();
  const matching = search ? exams.filter((e) => titleMatches(e, search)) : exams;
  const allMatchingOn = matching.length > 0 && matching.every((e) => shownIds.has(e.id));
  const toggle = (id) => onPick(shownIds.has(id) ? [...shownIds].filter((x) => x !== id) : [...shownIds, id]);
  const toggleMatching = () => {
    const ids = new Set(matching.map((e) => e.id));
    onPick(allMatchingOn ? [...shownIds].filter((id) => !ids.has(id)) : [...new Set([...shownIds, ...ids])]);
  };
  return (
    <div className="nx-epick">
      <div className="nx-epick__presets" role="group" aria-label="בחירה מהירה">
        {LAST_OPTIONS.map((o) => (
          <button key={o.value} type="button" className={`nx-epick__preset${last === o.value ? ' is-on' : ''}`} aria-pressed={last === o.value} onClick={() => onPreset(o.value)}>
            {o.label}
          </button>
        ))}
      </div>
      <SearchInput value={q} onChange={setQ} placeholder="חיפוש מבחן — מספר, מסכת…" autoFocus />
      <div className="nx-epick__bar">
        <span>{search ? `${num(matching.length)} תוצאות` : `${num(exams.length)} מבחנים`} · נבחרו {num(shownIds.size)}</span>
        <span className="nx-row" style={{ gap: 2 }}>
          {matching.length ? (
            <Button size="sm" variant="ghost" onClick={toggleMatching}>
              {allMatchingOn ? (search ? 'הסרת התוצאות' : 'הסרת הכל') : (search ? 'בחירת כל התוצאות' : 'בחירת הכל')}
            </Button>
          ) : null}
          {shownIds.size ? <Button size="sm" variant="ghost" onClick={() => onPick([])}>ניקוי</Button> : null}
        </span>
      </div>
      <div className="nx-epick__list">
        {matching.map((e) => {
          const on = shownIds.has(e.id);
          return (
            <label key={e.id} className={`nx-epick__row${on ? ' is-on' : ''}`} title={e.title}>
              <input type="checkbox" className="ck-checkbox" checked={on} onChange={() => toggle(e.id)} />
              <span className="nx-epick__title">{shortExamTitle(e, withFrame)}</span>
              <span className="nx-epick__n">{num(takenCount[e.id] || 0)} נבחנים</span>
            </label>
          );
        })}
        {!matching.length ? <span className="ck-muted nx-epick__empty">לא נמצאו מבחנים</span> : null}
      </div>
    </div>
  );
}

/** one number in the stats strip; with onClick it toggles a filter */
function Stat({ label, value, sub, tone, active, onClick }) {
  const Tagname = onClick ? 'button' : 'div';
  return (
    <Tagname
      type={onClick ? 'button' : undefined}
      className={`nx-rstat${onClick ? ' is-click' : ''}${active ? ' is-on' : ''}`}
      data-tone={tone}
      aria-pressed={onClick ? active : undefined}
      onClick={onClick}
    >
      <span className="nx-rstat__label">{label}</span>
      <b className="nx-rstat__num">{value}</b>
      {sub ? <span className="nx-rstat__sub">{sub}</span> : null}
    </Tagname>
  );
}

function ExamCell({ res, cellMode }) {
  if (!res) return <span className="ck-muted">—</span>;
  const title = [
    `ציון: ${scoreText(res)}`,
    res.f ? `נבחן: ${formatHebrewDateTime(res.f)}` : '',
    CHANNEL_LABELS[res.c] ? `ערוץ: ${CHANNEL_LABELS[res.c]}` : '',
    res.n > 1 ? `${res.n} ניסיונות` : '',
  ].filter(Boolean).join('\n');
  if (cellMode === 'score') {
    return <Tag tone={isHigh(res) ? 'success' : 'neutral'} title={title}><b>{scoreText(res)}</b></Tag>;
  }
  return <Tag tone="success" icon="check" title={title}>נבחן</Tag>;
}

export default function ExamineesReportNext() {
  const { hasPermission } = useAuth();
  const [broadcastTarget, setBroadcastTarget] = useState(null); // { memberIds, label }
  const [filters, setFilters] = useState(readStored);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [coverage, setCoverage] = useState('');
  const [query, setQuery] = useState(EMPTY_QUERY);
  const [selectedId, setSelectedId] = useState(null);
  const [statusMember, setStatusMember] = useState(null);
  const { frame, last, from, to, population, cellMode } = filters;
  const includeAll = population === 'all';

  const patch = (p) => {
    setFilters((f) => ({ ...f, ...p }));
    setQuery((q) => ({ ...q, page: 1 }));
  };
  useEffect(() => {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(filters)); } catch { /* per-viewer convenience only */ }
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: res } = await api.get('/american-exams/examinees-report', { params: includeAll ? { includeAll: 1 } : {} });
      setData(expandReport(res));
    } catch (e) {
      setError(apiError(e, 'שגיאה בטעינת דו"ח הנבחנים'));
    } finally {
      setLoading(false);
    }
  }, [includeAll]);
  useEffect(() => { load(); }, [load]);

  const statusSaved = (m) => {
    const id = String(m._id);
    const fields = { occupation: m.occupation || '', studyFormat: m.studyFormat || '', studyPartner: m.studyPartner || '', version: m.version };
    setData((d) => (d ? { ...d, members: d.members.map((x) => (String(x._id) === id ? { ...x, ...fields } : x)) } : d));
    setStatusMember(null);
  };

  // date range: drop results finished outside it, and offer only exams someone took inside it
  const members = useMemo(() => {
    const all = data?.members || [];
    if (!from && !to) return all;
    return all.map((m) => ({
      ...m,
      r: Object.fromEntries(Object.entries(m.r).filter(([, res]) => inDateRange(res, from, to))),
    }));
  }, [data, from, to]);
  const exams = useMemo(() => {
    const all = data?.exams || [];
    if (!from && !to) return all;
    const taken = new Set(members.flatMap((m) => Object.keys(m.r)));
    return all.filter((e) => taken.has(e.id));
  }, [data, members, from, to]);
  const trackExams = useMemo(() => (frame ? exams.filter((e) => e.frame === frame) : exams), [exams, frame]);
  const shownExams = useMemo(() => pickExams(trackExams, filters), [trackExams, filters]);
  const shownSet = useMemo(() => new Set(shownExams.map((e) => e.id)), [shownExams]);
  const examCountByFrame = useMemo(() => exams.reduce((acc, e) => ({ ...acc, [e.frame]: (acc[e.frame] || 0) + 1 }), {}), [exams]);
  const takenCount = useMemo(() => {
    const out = {};
    members.forEach((m) => Object.keys(m.r).forEach((id) => { out[id] = (out[id] || 0) + 1; }));
    return out;
  }, [members]);

  /* ── rows: the population, each with its stats over the shown exams ── */
  const baseRows = useMemo(() => {
    const shownIds = shownExams.map((e) => e.id);
    const trackIds = trackExams.map((e) => e.id);
    return members.reduce((out, m) => {
      const took = shownIds.filter((id) => m.r[id]);
      if (population === 'shown' && !took.length) return out;
      if (population === 'ever' && !trackIds.some((id) => m.r[id])) return out;
      const scores = took.map((id) => m.r[id]);
      const lastAt = scores.reduce((d, res) => (res.f && (!d || res.f > d) ? res.f : d), null);
      out.push({
        ...m,
        _taken: took.length,
        _avg: scores.length ? Math.round((scores.reduce((s, res) => s + (res.x ? (res.s / res.x) * 100 : res.s), 0) / scores.length) * 10) / 10 : null,
        _lastAt: lastAt,
        _coverage: !took.length ? 'none' : took.length === shownIds.length ? 'full' : 'partial',
      });
      return out;
    }, []);
  }, [members, shownExams, trackExams, population]);

  /* ── columns ── */
  const columns = useMemo(() => {
    const total = shownExams.length;
    const examCols = shownExams.map((e) => ({
      key: `${EXAM_KEY_PREFIX}${e.id}`,
      label: shortExamTitle(e, !frame),
      type: 'enum',
      searchable: false,
      sum: true,
      getValue: (r) => (r.r[e.id] ? 'נבחן' : 'לא נבחן'),
      exportValue: (r) => {
        const res = r.r[e.id];
        if (!res) return '';
        return cellMode === 'score' ? scoreText(res) : 'V';
      },
      render: (r) => <ExamCell res={r.r[e.id]} cellMode={cellMode} />,
    }));
    return [
      {
        key: '_name',
        label: 'נבחן',
        getValue: (r) => [memberName(r), r.idNumber, r.phone, r.city].filter(Boolean).join(' '),
        exportValue: memberName,
        render: (r) => (
          <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <button type="button" className="nx-link" onClick={(ev) => { ev.stopPropagation(); setSelectedId(String(r._id)); }}>
              <b>{memberName(r)}</b>
            </button>
            <small className="ck-muted">{[r.phone, r.idNumber ? `ת.ז. ${r.idNumber}` : ''].filter(Boolean).join(' · ') || '—'}</small>
          </span>
        ),
      },
      {
        key: 'occupation',
        label: 'סטטוס',
        type: 'enum',
        getValue: (r) => r.occupation || 'ללא סטטוס',
        exportValue: (r) => r.occupation || '',
        render: (r) => {
          const missing = studyMissing(r);
          return (
            <button type="button" className="nx-link" title="עדכון סטטוס" onClick={(ev) => { ev.stopPropagation(); setStatusMember(r); }}>
              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                {r.occupation ? <Tag>{r.occupation}</Tag> : <span className="ck-muted">בחר סטטוס</span>}
                {missing ? <Tag tone="warning" icon="alert">{missing}</Tag> : null}
              </span>
            </button>
          );
        },
      },
      {
        key: 'studyFormat',
        label: 'חברותא / שיעור',
        type: 'enum',
        getValue: (r) => (isWorking(r.occupation) ? r.studyFormat || 'לא צוין' : '—'),
        exportValue: (r) => (isWorking(r.occupation) ? r.studyFormat || '' : ''),
        render: (r) => (isWorking(r.occupation) && STUDY_FORMATS.includes(r.studyFormat)
          ? <Tag tone={r.studyFormat === 'שיעור' ? 'info' : 'violet'}>{r.studyFormat}</Tag>
          : <span className="ck-muted">—</span>),
      },
      {
        key: 'studyPartner',
        label: 'חברותא / מג"ש',
        getValue: (r) => (isWorking(r.occupation) ? r.studyPartner || '' : ''),
        render: (r) => (isWorking(r.occupation) && r.studyPartner
          ? <span title={partnerLabel(r.studyFormat)}>{r.studyPartner}</span>
          : <span className="ck-muted">—</span>),
      },
      { key: 'city', label: 'עיר', type: 'enum', render: (r) => r.city || <span className="ck-muted">—</span> },
      { key: 'fatherName', label: 'שם האב' },
      { key: 'idNumber', label: 'ת.ז.', ltr: true, copy: true },
      { key: 'phone', label: 'טלפון', ltr: true, copy: true },
      { key: 'personalCode', label: 'קוד אישי', type: 'number', ltr: true },
      {
        key: '_taken',
        label: 'נבחן ב־',
        type: 'number',
        exportValue: (r) => `${r._taken}/${total}`,
        render: (r) => (
          <Tag tone={r._coverage === 'full' ? 'success' : r._coverage === 'none' ? 'danger' : 'warning'}>
            <b>{`${r._taken}/${total}`}</b>
          </Tag>
        ),
      },
      {
        key: '_avg',
        label: 'ממוצע %',
        type: 'number',
        render: (r) => (r._avg == null ? <span className="ck-muted">—</span> : r._avg),
      },
      {
        key: '_lastAt',
        label: 'נבחן לאחרונה',
        type: 'date',
        exportValue: (r) => (r._lastAt ? formatHebrewDateTime(r._lastAt) : ''),
        render: (r) => (r._lastAt ? formatHebrewDateTime(r._lastAt) : <span className="ck-muted">—</span>),
      },
      ...examCols,
    ];
  }, [shownExams, frame, cellMode]);

  // a column filter on an exam that is no longer shown would silently filter nothing — drop it
  useEffect(() => {
    const keys = new Set(columns.map((c) => c.key));
    setQuery((q) => {
      const stale = Object.keys(q.filters || {}).filter((k) => !keys.has(k));
      if (!stale.length) return q;
      const next = { ...q.filters };
      stale.forEach((k) => delete next[k]);
      return { ...q, filters: next, page: 1 };
    });
  }, [columns]);

  /* ── what the table shows, and the stats over it ── */
  const queried = useMemo(() => applyClientQuery(baseRows, columns, { ...query, sort: null }), [baseRows, columns, query]);
  const tableRows = useMemo(() => (coverage ? baseRows.filter((r) => r._coverage === coverage) : baseRows), [baseRows, coverage]);
  const visible = useMemo(() => (coverage ? queried.filter((r) => r._coverage === coverage) : queried), [queried, coverage]);

  const stats = useMemo(() => {
    const s = { rows: queried.length, full: 0, partial: 0, none: 0, cells: 0, scoreSum: 0, high: 0 };
    queried.forEach((r) => {
      s[r._coverage] += 1;
      shownExams.forEach((e) => {
        const res = r.r[e.id];
        if (!res) return;
        s.cells += 1;
        s.scoreSum += res.x ? (res.s / res.x) * 100 : res.s;
        if (isHigh(res)) s.high += 1;
      });
    });
    s.possible = queried.length * shownExams.length;
    s.avg = s.cells ? Math.round((s.scoreSum / s.cells) * 10) / 10 : null;
    return s;
  }, [queried, shownExams]);

  const footerTotals = useMemo(() => Object.fromEntries(shownExams.map((e) => [
    `${EXAM_KEY_PREFIX}${e.id}`,
    visible.reduce((n, r) => n + (r.r[e.id] ? 1 : 0), 0),
  ])), [shownExams, visible]);

  const toggleCoverage = (key) => {
    setCoverage((c) => (c === key ? '' : key));
    setQuery((q) => ({ ...q, page: 1 }));
  };
  const resetAll = () => {
    setFilters(DEFAULTS);
    setCoverage('');
    setQuery(EMPTY_QUERY);
  };

  /* ── what each filter button says ── */
  const frameLabel = frame ? EXAMINEES_FRAME_LABELS[frame] : 'כל המסלולים';
  const populationLabel = POPULATIONS.find((p) => p.value === population)?.label || '';
  const examsValue = last === 'custom'
    ? (shownExams.length === 1 ? shortExamTitle(shownExams[0], !frame) : `${num(shownExams.length)} נבחרו`)
    : `${LAST_OPTIONS.find((o) => o.value === last)?.label || ''}${frame || last === 'all' ? '' : ' בכל מסלול'}`;
  const datePreset = DATE_PRESETS.find((d) => d.days && !to && from === daysAgo(d.days));
  const dateValue = !from && !to ? 'הכל' : datePreset ? datePreset.label : `${fmtDay(from)} – ${fmtDay(to)}`;
  const isDefault = !coverage && ['frame', 'last', 'from', 'to', 'population'].every((k) => filters[k] === DEFAULTS[k]);
  const busy = loading && !data;
  const exportName = `דוח-נבחנים-${frameLabel}`;

  return (
    <>
      <PageHeader
        title='דו"ח נבחנים'
        sub="מי נבחן באילו מבחנים — לפי מסלול ומבחנים נבחרים"
        primary={hasPermission('members', 'update') ? {
          label: `שיגור הודעה ל־${num(visible.length)}`,
          icon: 'mic',
          onClick: () => setBroadcastTarget({ memberIds: visible.map((r) => String(r._id)), label: `${num(visible.length)} נבחנים לפי הסינון` }),
        } : undefined}
        more={[{ key: 'refresh', label: 'רענון', icon: 'refresh', onClick: load }]}
      />

      {error ? <Banner tone="danger" icon="alert" action={<Button size="sm" onClick={load}>נסו שוב</Button>}>{error}</Banner> : null}

      <div className="nx-fbar" role="toolbar" aria-label="סינון הדו״ח">
        <FilterButton label="מסלול" value={frameLabel} changed={frame !== DEFAULTS.frame} width={240}>
          {(close) => (
            <OptionList
              value={frame}
              onPick={(v) => { patch({ frame: v, picked: [] }); close(); }}
              options={[{ value: '', label: 'כל המסלולים', count: exams.length }, ...FRAMES.map((f) => ({ ...f, count: examCountByFrame[f.value] || 0 }))]}
            />
          )}
        </FilterButton>

        <FilterButton label="מבחנים" value={examsValue} changed={last !== DEFAULTS.last} width={440}>
          <ExamPicker
            exams={trackExams}
            shownIds={shownSet}
            last={last}
            withFrame={!frame}
            takenCount={takenCount}
            onPreset={(v) => patch({ last: v })}
            onPick={(ids) => patch({ last: 'custom', picked: ids })}
          />
        </FilterButton>

        <FilterButton label="תאריכים" value={dateValue} changed={!!(from || to)} width={300}>
          {(close) => (
            <>
              <OptionList
                value={!from && !to ? '' : datePreset?.key ?? null}
                onPick={(k) => {
                  const d = DATE_PRESETS.find((x) => x.key === k);
                  patch({ from: d?.days ? daysAgo(d.days) : '', to: '' });
                  close();
                }}
                options={DATE_PRESETS.map((d) => ({ value: d.key, label: d.label }))}
              />
              <div className="nx-fdates">
                <span className="ck-field__label">טווח מותאם — מועד הבחינה</span>
                <div className="nx-report-dates">
                  <input type="date" className="ck-input" aria-label="מתאריך" value={from} max={to || undefined} onChange={(e) => patch({ from: e.target.value })} />
                  <span className="ck-muted">עד</span>
                  <input type="date" className="ck-input" aria-label="עד תאריך" value={to} min={from || undefined} onChange={(e) => patch({ to: e.target.value })} />
                </div>
              </div>
            </>
          )}
        </FilterButton>

        <FilterButton label="נבחנים" value={populationLabel} changed={population !== DEFAULTS.population} width={330}>
          {(close) => <OptionList value={population} onPick={(v) => { patch({ population: v }); close(); }} options={POPULATIONS} />}
        </FilterButton>

        <span className="nx-fbar__end">
          <Segmented ariaLabel="תצוגת התאים" value={cellMode} onChange={(v) => patch({ cellMode: v })} items={CELL_MODES} />
          {!isDefault ? <Button size="sm" variant="ghost" icon="x" onClick={resetAll}>איפוס</Button> : null}
        </span>
      </div>

      <div className="nx-rstats" aria-busy={busy}>
        <Stat label="נבחנים בדו״ח" value={busy ? '…' : num(stats.rows)} tone="primary" />
        <Stat label={COVERAGE.full} value={busy ? '…' : num(stats.full)} sub={pct(stats.full, stats.rows)} tone="success" active={coverage === 'full'} onClick={() => toggleCoverage('full')} />
        <Stat label={COVERAGE.partial} value={busy ? '…' : num(stats.partial)} sub={pct(stats.partial, stats.rows)} tone="warning" active={coverage === 'partial'} onClick={() => toggleCoverage('partial')} />
        <Stat label={COVERAGE.none} value={busy ? '…' : num(stats.none)} sub={pct(stats.none, stats.rows)} tone="danger" active={coverage === 'none'} onClick={() => toggleCoverage('none')} />
        <Stat label="מבחנים שמולאו" value={busy ? '…' : num(stats.cells)} sub={`השתתפות ${pct(stats.cells, stats.possible)}`} tone="info" />
        <Stat label="ציון ממוצע" value={busy || stats.avg == null ? '—' : `${stats.avg}%`} sub={`${num(stats.high)} ב־90%+`} tone="violet" />
      </div>

      <DataTable
        mode="client"
        rows={tableRows}
        rowKey="_id"
        columns={columns}
        loading={busy}
        query={query}
        onQueryChange={setQuery}
        footerTotals={footerTotals}
        // v3: the status column is the member's occupation (+ חברותא / שיעור) — a new key so saved layouts (occupation hidden) don't keep hiding it
        storageKey="bchinot-examinees-report-v3"
        layoutDefaults={{ pinned: ['_name'], hidden: ['fatherName', 'idNumber', 'phone', 'personalCode'] }}
        selectable={false}
        searchPlaceholder="חיפוש לפי שם, ת.ז., טלפון, עיר, חברותא…"
        extraChips={coverage ? [{ key: 'coverage', label: COVERAGE[coverage], onRemove: () => toggleCoverage(coverage) }] : []}
        onClearAll={() => setCoverage('')}
        exportFilename={exportName}
        onExport={({ format, columns: cols }) => exportRows(visible, cols, { format, filename: exportName })}
        emptyTitle={shownExams.length ? 'אין נבחנים להצגה' : 'לא נבחרו מבחנים'}
        emptyText={shownExams.length ? 'אף נבחן לא תואם את הסינון.' : 'בחרו מבחנים בכפתור «מבחנים» שלמעלה.'}
        onRowClick={(r) => setSelectedId(String(r._id))}
        rowActions={(r) => [
          { key: 'status', icon: 'edit', label: 'עדכון סטטוס', onClick: () => setStatusMember(r) },
          { key: 'card', icon: 'user', label: 'כרטיס חבר', onClick: () => setSelectedId(String(r._id)) },
        ]}
      />

      <MessageBroadcastDialog target={broadcastTarget} onClose={() => setBroadcastTarget(null)} />

      <StudyStatusDialogNext member={statusMember} onClose={() => setStatusMember(null)} onSaved={statusSaved} />

      <Sheet open={!!selectedId} onClose={() => setSelectedId(null)} width={1080} ariaLabel="כרטיס חבר">
        {selectedId ? <div className="nx-sheet-body"><MemberCardNext key={selectedId} recordId={selectedId} onClose={() => setSelectedId(null)} /></div> : null}
      </Sheet>
    </>
  );
}

/** the old design has no crm-kit/next theme around the page — supply one */
export function ExamineesReportLegacy() {
  return (
    // eslint-disable-next-line react/style-prop-object -- CkThemeProvider's `style` is the design style name
    <CkThemeProvider style="modern" mode="light" accent="#4F46E5" className="bchinot-next">
      <CkToastProvider>
        <div className="nx-stack"><ExamineesReportNext /></div>
      </CkToastProvider>
    </CkThemeProvider>
  );
}
